import { FormikErrors, useFormik } from "formik";
import { useEffect, useRef, useState, useMemo } from "react";
import * as yup from "yup";
import crossIcon from "../../assets/svg/crossBtn.svg";

import { renderError, renderImagePreview } from "../../utils/utils";

import { Editor } from "@tinymce/tinymce-react";
import Loader from "components/loader";
import uploadIcon from "../../assets/svg/upload-icon.svg";

import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  addInitiatives,
  getInitiativesById,
  updateInitiatives,
} from "../../axios/api";
import { toast } from "react-toastify";

const AddFlInitiative = () => {
  const editorRef = useRef(null);
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const id = location?.state;

  const [initiativeData, setInitiativeData] = useState<any>();
  const [editorContent, setEditorContent] = useState("");
  const [popupMessage, setPopupMessage] = useState("");
  const [popupIconMessage, setPopupIconMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const styleForEditor =
    ':root { --background-color: #fff; --boxshadow-color: #00000030; } body { font-family: Poppins, sans-serif; } .dark { --background-color: #1F2937; --boxshadow-color: #BDBDBD75; color: #fff; } details > summary::-webkit-details-marker { display: none; } summary { background-color: var(--background-color); padding: 10px; cursor: pointer; margin-right: 0.5rem; } details summary { list-style: none; cursor: pointer; position: relative; } details p { padding: 0.5rem; } details summary::marker { display: none; } details summary::before { content: "▼"; display: inline-block; width: 1em; height: 1em; position: absolute; top: 20%; right: 5%; transition: all 0.3s ease; } details[open] summary::before { transform: rotate(180deg); top: 40%; } details { width: 92%; margin-left: calc(4% - 6px); box-shadow: 0 0 10px var(--boxshadow-color); padding: 5px; background-color: var(--background-color); cursor: pointer; margin-bottom: 15px; }';
  const log = () => {
    if (editorRef.current) {
      console.log(editorRef.current.getContent());
    }
  };
  const fileInputRef = useRef(null);
  const iconInputRef = useRef(null);

  const formik = useFormik({
    initialValues: {
      InitiativeTitle: "",
      description: "",
      BannerImage: null,
      BannerImageUrl: "",
      iconImage: null,
      iconImageUrl: "",
      abbreviation: "",
    },
    onSubmit: async (values) => {
      try {
        setLoading(true);
        // console.log("values", values);
        const formData = new FormData();
        const fullContent = `<html><head><link rel="preconnect" href="https://fonts.googleapis.com"><link rel="preconnect" href="https://fonts.gstatic.com" crossorigin><link href="https://fonts.googleapis.com/css2?family=Poppins:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&display=swap" rel="stylesheet"><meta name="viewport" content="width=device-width, initial-scale=1.0"><meta http-equiv="Content-Security-Policy" content="default-src *; img-src * 'self' data:; script-src 'unsafe-inline' 'unsafe-eval' *; style-src 'unsafe-inline' *;"><style>${styleForEditor}</style></head><body>${editorContent}</body></html>`;
        formData.append("locale", "1");
        formData.append("abbreviation", values.abbreviation);
        formData.append("title", encodeURI(values.InitiativeTitle));
        formData.append("description", fullContent);
        formData.append(
          "image",
          (values?.BannerImage && values.BannerImage) ||
            (values?.BannerImageUrl &&
              values.BannerImageUrl.replace(
                /(https?:\/\/)?(?:\d{1,3}\.){3}\d{1,3}/,
                ""
              )) ||
            ""
        );
        formData.append("financialInitiativeId", values.abbreviation);
        formData.append("initiative_icon", values.iconImage);
        // formData.append("status", "active");
        // console.log("Form Data:", formData);
        let responseData: any;
        if (id) {
          responseData = await updateInitiatives(formData, id);
          if (responseData?.data?.success) {
            toast.success("Initiatives Updated Sucessfully");
            navigate("/admin/fl-initiatives");
          } else {
            toast.error(responseData?.data?.message);
          }
        } else {
          responseData = await addInitiatives(formData);
          if (responseData?.data?.success) {
            toast.success("Initiatives Added Sucessfully");
            navigate("/admin/fl-initiatives");
          } else {
            toast.error(responseData?.data?.message);
          }
        }
        if (!responseData?.data?.success) {
          // console.log("Response", responseData);
          toast.error(responseData?.response?.data?.message);
        }
        // console.log("API Response:", responseData);
      } catch (error) {
        console.error("API Error:", error);
        toast.error("Something went wrong" + error);
      } finally {
        setLoading(false);
      }
    },
    validationSchema: yup.object({
      InitiativeTitle: yup
        .string()
        .trim()
        .required("Initiative Title is required"),
      description: yup.string().trim().required("Description is required"),
    }),
  });

  const checkImage = useMemo(
    () =>
      renderImagePreview(
        formik.values.BannerImage,
        formik.values.BannerImageUrl
      ),
    [formik.values.BannerImage, formik.values.BannerImageUrl]
  );

  const checkIconImage = useMemo(
    () =>
      renderImagePreview(formik.values.iconImage, formik.values.iconImageUrl),
    [formik.values.iconImage, formik.values.iconImageUrl]
  );

  // Trigger fileInput on click on div
  const handleDivClick = (ref: any) => {
    ref.current.click();
  };

  const handleDragOver = (e: any) => {
    e.preventDefault();
  };

  const handleDrop = (e: any, fieldName: any) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file.size > 100 * 1024) {
      setPopupMessage("Image size should be less than 100kb.");
      formik.setFieldValue(fieldName, null);
    } else {
      setPopupMessage("");
      formik.setFieldValue(fieldName, file);
    }
  };

  const handleIconDrop = (e: any, fieldName: any) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file.size > 10 * 1024) {
      setPopupIconMessage("Image size should be less than 10kb.");
      formik.setFieldValue(fieldName, null);
    } else {
      setPopupIconMessage("");
      formik.setFieldValue(fieldName, file);
    }
  };

  const handleEditorChange = (content: any, editor: any) => {
    setEditorContent(content);
    formik.setFieldValue("description", content);
  };

  useEffect(() => {
    const fetchInitiative = async (id: string) => {
      try {
        setLoading(true);
        const initiativeDataResp = await getInitiativesById(id);
        setInitiativeData(initiativeDataResp?.data?.data?.FlInitiative);
      } catch (error) {
        console.log("error", error);
      } finally {
        setLoading(false);
      }
    };
    if (id) {
      fetchInitiative(id);
    }
  }, [id]);

  useEffect(() => {
    if (initiativeData) {
      formik.setValues({
        InitiativeTitle: initiativeData.title,
        description: initiativeData.description,
        BannerImage: null,
        BannerImageUrl: initiativeData.image,
        iconImage: null,
        iconImageUrl: initiativeData.initiative_icon,
        abbreviation: initiativeData.abbreviation,
      });
      setEditorContent(initiativeData.description);
    }
  }, [initiativeData]);
  return (
    <div className="m-3 bg-[#FFFFFF] p-3">
      {loading ? (
        <Loader />
      ) : (
        <form onSubmit={formik.handleSubmit}>
          <div className="my-5 flex w-11/12 flex-col">
            <div className="my-2 flex w-full flex-col items-start justify-center gap-2">
              <label htmlFor="InitiativeTitle" className="text-xl font-medium">
                Initiative Title
              </label>
              <input
                type="text"
                name="InitiativeTitle"
                id="InitiativeTitle"
                value={formik.values.InitiativeTitle}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="Enter Initiative title here"
                className="h-10 w-[40%] rounded-md border-[2px] border-[#E4E6EF] bg-[transparent] p-2"
              />
              {formik.touched.InitiativeTitle &&
              formik.errors.InitiativeTitle ? (
                <div className="text-lg text-red-500">
                  {renderError(formik.errors.InitiativeTitle)}
                </div>
              ) : null}
            </div>
            <div className="my-2 flex w-full flex-col items-start justify-center gap-2">
              <label htmlFor="abbreviation" className="text-xl font-medium">
                Initiative Abbbrevation
              </label>
              <input
                type="text"
                name="abbreviation"
                id="abbreviation"
                value={formik.values.abbreviation}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="Enter Initiative title here"
                className="h-10 w-[40%] rounded-md border-[2px] border-[#E4E6EF] bg-[transparent] p-2"
              />
              {formik.touched.abbreviation && formik.errors.abbreviation ? (
                <div className="text-lg text-red-500">
                  {renderError(formik.errors.abbreviation)}
                </div>
              ) : null}
            </div>
            <div className="my-5 flex items-center gap-9 p-1">
              <div className="w-[35%]">
                <div className="mb-1 flex flex-col">
                  <label
                    htmlFor="BannerImageUrl"
                    className=" text-xl font-medium"
                  >
                    Initiative Banner Image
                  </label>
                  <span className=" text-sm text-red-500">
                    (Image resolution should be 350*150)
                  </span>
                  <span className=" text-sm text-red-500">
                    (Valid Image Formats: jpg, jpeg, png and gif)
                  </span>
                </div>
                <div className="relative my-3 mb-5 flex w-[100%] items-center justify-between">
                  <input
                    // className="absolute bottom-0 left-8 -z-10 w-20"
                    className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                    // required={
                    //   !formik.values.BannerImageUrl &&
                    //   !formik.values.BannerImage
                    // }
                    name="BannerImageUrl"
                    id="BannerImageUrl"
                    ref={fileInputRef}
                    type="file"
                    accept=".jpg, .jpeg, .png, .gif"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file?.size > 100 * 1024) {
                        setPopupMessage(
                          "Image size should be less than 100kb."
                        );
                        formik.setFieldValue("BannerImageUrl", "");
                      } else {
                        setPopupMessage("");
                        formik.setFieldValue("BannerImageUrl", file?.name);
                        formik.setFieldValue("BannerImage", file);
                      }
                    }}
                  />
                  <div
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, "BannerImage")}
                    onClick={() => handleDivClick(fileInputRef)}
                    className="relative flex h-44 w-full items-center justify-center overflow-hidden rounded border-4 border-dashed"
                  >
                    {formik.values.BannerImageUrl ||
                    formik.values.BannerImage ? (
                      <>
                        <div className="relative flex items-center p-2">
                          {(formik.values.BannerImage ||
                            formik.values.BannerImageUrl) && (
                            <img
                              className="object-scale-down"
                              alt="BannerImage"
                              src={checkImage}
                            />
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            formik.setFieldValue("BannerImage", null);
                            formik.setFieldValue("BannerImageUrl", null);
                            fileInputRef.current.value = null;
                          }}
                          className="absolute right-1 top-1 z-10"
                        >
                          <img src={crossIcon} alt="Cross Icon" />
                        </button>
                      </>
                    ) : (
                      <div
                        id="imageDiv"
                        className="flex flex-col items-center justify-center text-center font-medium hover:cursor-pointer"
                      >
                        <img
                          className="mb-2 w-1/4"
                          src={uploadIcon}
                          alt="Upload Icon"
                        />
                        <span>
                          <span className="text-blue-500">Click here </span>to
                          Upload
                        </span>
                        <span> Banner Image</span>
                      </div>
                    )}
                  </div>
                </div>
                {popupMessage && (
                  <div className="top-full mt-2 text-center text-red-500">
                    {popupMessage}
                  </div>
                )}
              </div>

              <div className="w-[35%]">
                <div className="mb-1 flex flex-col">
                  <label
                    htmlFor="iconImageUrl"
                    className=" text-xl font-medium"
                  >
                    Initiative Icon
                  </label>
                  <span className=" text-sm text-red-500">
                    (Image resolution should be 100*100)
                  </span>
                  <span className=" text-sm text-red-500">
                    (Valid Image Formats: jpg, jpeg, png and gif)
                  </span>
                </div>
                {/* Icon Image Upload */}
                <div className="relative my-3 mb-5 flex w-[100%] items-center justify-between">
                  <input
                    //className="absolute bottom-0 left-8 -z-10 w-20"
                    className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                    required={
                      !formik.values.iconImageUrl && !formik.values.iconImage
                    }
                    name="iconImageUrl"
                    id="iconImageUrl"
                    ref={iconInputRef}
                    type="file"
                    accept=".jpg, .jpeg, .png, .gif"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file?.size > 10 * 1024) {
                        setPopupIconMessage(
                          "Image size should be less than 10kb."
                        );
                        formik.setFieldValue("iconImageUrl", "");
                      } else {
                        setPopupIconMessage("");
                        formik.setFieldValue("iconImageUrl", file?.name);
                        formik.setFieldValue("iconImage", file);
                      }
                    }}
                  />
                  <div
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleIconDrop(e, "iconImage")}
                    onClick={() => handleDivClick(iconInputRef)}
                    //className="flex h-44 w-full items-center justify-center rounded border-4 border-dashed"
                    className="relative flex h-44 w-full items-center justify-center overflow-hidden rounded border-4 border-dashed"
                  >
                    {formik.values.iconImageUrl || formik.values.iconImage ? (
                      <>
                        <div className="relative flex items-center p-2">
                          {(formik.values.iconImage ||
                            formik.values.iconImageUrl) && (
                            <img
                              className=""
                              alt="iconImage"
                              src={checkIconImage}
                            />
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            formik.setFieldValue("iconImage", null);
                            formik.setFieldValue("iconImageUrl", "");
                            iconInputRef.current.value = null;
                          }}
                          className="absolute right-1 top-1 z-10"
                        >
                          <img src={crossIcon} alt="Cross Icon" />
                        </button>
                      </>
                    ) : (
                      <div
                        id="imageDiv"
                        className="flex flex-col items-center justify-center text-center font-medium hover:cursor-pointer"
                      >
                        <img
                          className="mb-2 w-1/4"
                          src={uploadIcon}
                          alt="Upload Icon"
                        />
                        <span>
                          <span className="text-blue-500">Click here </span>to
                          Upload
                        </span>
                        <span>Initiative Icon</span>
                      </div>
                    )}
                  </div>
                </div>
                {popupIconMessage && (
                  <div className="top-full mt-2 text-center text-red-500">
                    {popupIconMessage}
                  </div>
                )}
              </div>
            </div>
            <div className="my-5">
              <label htmlFor="" className="my-2 text-xl font-medium">
                FL Initiative Description Text
              </label>
              <div className="my-3">
                <Editor
                  onEditorChange={handleEditorChange}
                  tinymceScriptSrc="/tinymce/tinymce.min.js"
                  licenseKey="gpl"
                  onInit={(_evt, editor) => (editorRef.current = editor)}
                  value={editorContent}
                  init={{
                    relative_urls: false,
                    remove_script_host: false,
                    convert_urls: true,
                    height: 500,
                    menubar: false,
                    plugins: [
                      "advlist",
                      "autolink",
                      "lists",
                      "link",
                      "preview",
                      "anchor",
                      "visualblocks",
                      "code",
                      "fullscreen",
                      "insertdatetime",
                      "image",
                      "help",
                      "wordcount",
                      "accordion",
                      "preview",
                      "table",
                      "emoticons", // Custom plugin should be correctly referenced
                    ],
                    toolbar: [
                      "undo redo | blocks blockquote | " +
                        "bold italic underline strikethrough superscript subscript | forecolor backcolor | " +
                        "align bullist numlist | outdent indent | " +
                        "image link | table accordion | emoticons | removeformat | preview | help", // Ensure toolbar buttons are added
                    ],
                    toolbar_sticky: true,
                    toolbar_mode: "sliding",
                    content_style: styleForEditor,
                    // setup: (editor) => {
                    //   editor.on("change", (e) => {
                    //     handleEditorChange(
                    //       editor.getContent({ format: "html" }),
                    //       editor
                    //     );
                    //   });
                    // },
                  }}
                />
              </div>
              {formik.touched.description && formik.errors.description ? (
                <div>{renderError(formik.errors.description)}</div>
              ) : null}
            </div>
            <div>
              <button
                name="submit"
                className="float-right my-5 w-24 rounded bg-[#008BD2] p-2 text-xl font-bold text-white"
                type="submit"
              >
                Submit
              </button>
            </div>
          </div>
        </form>
      )}
    </div>
  );
};

export default AddFlInitiative;
